import { z } from 'zod';

/**
 * Chronologie agrégée du dossier patient : tout ce qui est arrivé à ce dossier, quel que soit le
 * module qui l'a produit, sur une seule ligne de temps décroissante.
 *
 * Deux règles tiennent ce contrat.
 *
 * — **Aucune donnée clinique ni identifiante dans `summary`.** On n'y met que des champs techniques :
 *   statuts, catégories, identifiants, montants en centimes. Le libellé humain est construit par le
 *   front à partir de ses traductions. C'est ce qui permet de tracer et de journaliser un appel sans
 *   jamais laisser fuir un motif de consultation ou un nom de fichier.
 *
 * — **Pagination par curseur**, jamais par numéro de page : la source est une union de six tables et
 *   un `offset` sur une union relit tout ce qui précède à chaque page.
 *
 * Deux lignes ne sont lues dans aucune table : elles sont **dérivées** à la lecture et se rangent
 * dans la même pagination.
 *
 * — `pediatric_summary`, le résumé « Bilan pédiatrique — 1ère année », daté du premier anniversaire
 *   et présent seulement quand il est passé et qu'une consultation de l'enfant porte un bilan.
 * — `birth`, la date de naissance du dossier, datée de ce jour, une seule fois, et absente quand
 *   le dossier n'en a pas.
 */

export const timelineItemKinds = [
  'appointment',
  'encounter',
  'document',
  'history',
  'invoice',
  'dunning',
  'pediatric_summary',
  'birth',
] as const;
export type TimelineItemKind = (typeof timelineItemKinds)[number];

/** Plafond dur : au-delà, la requête d'union cesse d'être bon marché. */
export const TIMELINE_MAX_LIMIT = 100;

/**
 * Filtres de l'écran, ceux de la V1 : antécédents et sphères se filtrent séparément alors qu'ils
 * sont une seule source, factures et relances vont ensemble, et le résumé pédiatrique comme la
 * date de naissance ont le leur.
 * Le filtre est appliqué par le serveur, sans quoi une page de vingt-cinq lignes filtrée après coup
 * pourrait n'en montrer aucune.
 */
export const timelineFilterTypes = [
  'encounter',
  'appointment',
  'antecedent',
  'sphere',
  'document',
  'billing',
  'pediatric',
  'birth',
] as const;
export type TimelineFilterType = (typeof timelineFilterTypes)[number];

/** Un ou plusieurs types séparés par des virgules : `?types=antecedent,sphere`. */
export const timelineTypesFilterSchema = z
  .string()
  .transform((value) => value.split(',').map((part) => part.trim()))
  .pipe(z.array(z.enum(timelineFilterTypes)).min(1).max(timelineFilterTypes.length));

export const timelineQuerySchema = z
  .object({
    /** Borne basse incluse, instant ISO 8601. */
    from: z.iso.datetime({ offset: true }).optional(),
    /** Borne haute exclue, instant ISO 8601. */
    to: z.iso.datetime({ offset: true }).optional(),
    types: timelineTypesFilterSchema.optional(),
    limit: z.coerce.number().int().min(1).max(TIMELINE_MAX_LIMIT).default(25),
    /** Curseur opaque rendu par la page précédente. Le client ne le fabrique jamais. */
    cursor: z.string().min(1).max(200).optional(),
  })
  .strict();
export type TimelineQuery = z.infer<typeof timelineQuerySchema>;

/** Rendez-vous. Le motif et les notes restent dans le module agenda. */
export interface TimelineAppointmentSummary {
  status: string;
  source: string;
}

/** Consultation. Le narratif ne sort pas d'ici. */
export interface TimelineEncounterSummary {
  status: string;
  practitionerMemberId: string;
}

/** Pièce jointe. Ni nom de fichier, ni description : ce sont des données de santé. */
export interface TimelineDocumentSummary {
  category: string;
  kind: string;
}

/** Antécédent ou sphère. Le titre et la description restent dans le dossier. */
export interface TimelineHistorySummary {
  entryKind: string;
  category: string | null;
  systemType: string | null;
}

/** Facture. Le numéro est une référence comptable, pas une donnée personnelle. */
export interface TimelineInvoiceSummary {
  number: string | null;
  status: string;
  totalCents: number;
}

/** Relance de paiement, rattachée à sa facture. */
export interface TimelineDunningSummary {
  level: number;
  status: string;
  invoiceId: string;
}

/**
 * Résumé pédiatrique de la première année. Deux compteurs, rien d'autre : le contenu se lit dans la
 * fenêtre qu'il ouvre. Son identifiant est celui du dossier — il y en a un par enfant.
 */
export interface TimelinePediatricSummary {
  /** Acquisitions psychomotrices et signes d'interaction cochés au bilan le plus récent. */
  acquisitionCount: number;
  /** Consultations de l'enfant qui portent un bilan. */
  assessmentCount: number;
}

/**
 * Date de naissance. Rien à transporter : le jour est `occurredAt`, le libellé est traduit par le
 * client. La date elle-même ne revient pas une seconde fois dans `summary`.
 */
export type TimelineBirthSummary = Record<string, never>;

export type TimelineItemSummary =
  | TimelineAppointmentSummary
  | TimelineEncounterSummary
  | TimelineDocumentSummary
  | TimelineHistorySummary
  | TimelineInvoiceSummary
  | TimelineDunningSummary
  | TimelinePediatricSummary
  | TimelineBirthSummary;

/** Une entrée de la chronologie. `kind` dit quelle forme prend `summary`. */
export type TimelineItemDto =
  | { kind: 'appointment'; id: string; occurredAt: string; summary: TimelineAppointmentSummary }
  | { kind: 'encounter'; id: string; occurredAt: string; summary: TimelineEncounterSummary }
  | { kind: 'document'; id: string; occurredAt: string; summary: TimelineDocumentSummary }
  | { kind: 'history'; id: string; occurredAt: string; summary: TimelineHistorySummary }
  | { kind: 'invoice'; id: string; occurredAt: string; summary: TimelineInvoiceSummary }
  | { kind: 'dunning'; id: string; occurredAt: string; summary: TimelineDunningSummary }
  | {
      kind: 'pediatric_summary';
      id: string;
      occurredAt: string;
      summary: TimelinePediatricSummary;
    }
  | { kind: 'birth'; id: string; occurredAt: string; summary: TimelineBirthSummary };

export interface TimelineDto {
  items: TimelineItemDto[];
  /** Curseur de la page suivante, `null` quand il n'y a plus rien après. */
  nextCursor: string | null;
}
